import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { fmtInt, fmtRelative } from '@/lib/format';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { getTgUser, haptic } from '@/lib/telegram';

export function CampaignsPage(): JSX.Element {
  const { data: campaigns, loading } = useAsync(() => api.listCampaigns(), []);
  const tg = getTgUser();
  const nav = useNavigate();

  const totalActive = (campaigns ?? []).filter((c) => c.status === 'running').length;
  const totalToday = (campaigns ?? []).reduce((s, c) => s + (c.todayPosts ?? 0), 0);

  return (
    <div className="space-y-8">
      {/* greeting */}
      <header className="pt-2">
        <p className="text-2xs uppercase tracking-widest text-ink-500">сегодня</p>
        <h1 className="font-display text-[28px] font-extrabold leading-tight text-ink-50">
          Привет, {tg?.first_name ?? 'нейрочаттер'}.
        </h1>
      </header>

      {/* HERO — большой блок с огромным числом, как в референсе */}
      <section className="card-hero relative p-7">
        {/* Декоративная кривая на фоне */}
        <svg
          className="pointer-events-none absolute -right-2 -top-4 z-0"
          width="240" height="200" viewBox="0 0 240 200" fill="none"
        >
          <defs>
            <linearGradient id="hero-line-grad" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.05" />
              <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          {/* основной плавный путь "вечно вверх" */}
          <path
            className="hero-line"
            d="M0 160 C 50 100, 90 150, 130 90 S 200 30, 240 10"
            stroke="url(#hero-line-grad)"
            strokeWidth="1.4"
            strokeLinecap="round"
            fill="none"
          />
          {/* точки на пути — лёгкое всплывание */}
          <circle className="hero-dot" cx="68" cy="115" r="3.2" fill="white" style={{animationDelay:'0s'}} />
          <circle className="hero-dot" cx="176" cy="58"  r="3.2" fill="white" style={{animationDelay:'1.2s'}} />
        </svg>
        {/* «Delta» badge на кривой */}
        <span className="delta absolute right-6 top-6 z-10">+{fmtInt(totalToday)} сегодня</span>

        <div className="relative z-10 mt-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/60">успешных посевов</p>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-display font-extrabold tracking-tight text-white" style={{fontSize:'76px',lineHeight:'76px',letterSpacing:'-0.03em'}}>
              {fmtInt((campaigns ?? []).reduce((s, c) => s + c.counts.posts, 0))}
            </span>
            <span className="text-base font-semibold text-white/60">посевов</span>
          </div>
          <p className="mt-3 text-sm text-white/70">
            {totalActive
              ? `${totalActive} активных кампан${totalActive === 1 ? 'ия' : totalActive < 5 ? 'ии' : 'ий'} · ${fmtInt(totalToday)} за сегодня`
              : 'Нет активных кампаний — создай первую'}
          </p>

          <button
            onClick={() => { haptic('medium'); nav('/campaigns/new'); }}
            className="btn-white mt-5"
          >
            <Icon name="plus" size={18} /> Запустить кампанию
          </button>
        </div>
      </section>

      {/* list */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between px-1">
          <h2 className="text-base font-bold text-ink-50">Мои и командные кампании</h2>
          <Link to="/campaigns" className="text-xs font-semibold text-accent-300">все</Link>
        </div>

        {loading && (
          <div className="space-y-3">
            <div className="skeleton h-28" />
            <div className="skeleton h-28" />
          </div>
        )}

        {!loading && (campaigns ?? []).length === 0 && (
          <EmptyState
            icon="sparkles"
            title="Пока нет кампаний"
            body="Создайте первую — назовите, добавьте чаты, дайте бриф для ИИ."
            cta={<button className="btn-primary" onClick={() => nav('/campaigns/new')}>Создать кампанию</button>}
          />
        )}

        {(campaigns ?? []).map((c) => (
          <Link
            key={c.id}
            to={`/campaigns/${c.id}`}
            onClick={() => haptic('light')}
            className="card relative block p-6 transition active:scale-[0.995]"
          >
            <div className="relative z-10 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <StatusPill status={c.status} />
                  <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-300">
                    {c.isOwned === false ? 'командная' : 'моя'}
                  </span>
                </div>
                <div className="mt-2 truncate text-lg font-bold text-ink-50">{c.name}</div>
                <div className="mt-0.5 text-2xs text-ink-500">создана {fmtRelative(c.createdAt)}</div>
              </div>
              <span className="text-ink-500 mt-1"><Icon name="chevron-right" size={20} /></span>
            </div>

            <div className="relative z-10 mt-6 grid grid-cols-4 gap-4">
              <Stat label="чаты"  value={c.counts.targets} />
              <Stat label="акки"  value={c.counts.accounts} />
              <Stat label="посевы" value={c.counts.posts} />
              <Stat label="ЛС"    value={c.counts.dms} highlight />
            </div>

            {typeof c.todayPosts === 'number' && c.todayLimit ? (
              <div className="relative z-10 mt-6">
                <div className="mb-1.5 flex items-center justify-between text-2xs uppercase tracking-wider text-ink-500">
                  <span>сегодня</span>
                  <span className="text-ink-100">{c.todayPosts} / {c.todayLimit}</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                  <div
                    className="h-full rounded-full bg-grad-primary"
                    style={{ width: `${Math.min(100, (c.todayPosts / c.todayLimit) * 100)}%` }}
                  />
                </div>
              </div>
            ) : null}
          </Link>
        ))}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }): JSX.Element {
  return (
    <div>
      <div className={`text-xl font-extrabold tracking-tight ${highlight ? 'text-accent-300' : 'text-ink-50'}`}>
        {fmtInt(value)}
      </div>
      <div className="text-2xs uppercase tracking-wider text-ink-500">{label}</div>
    </div>
  );
}
